import { useCallback, useDeferredValue, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Search } from "lucide-react";
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@ndd/ui";
import {
  SESSION_PATIENT_LINKS_FLAG,
  useFeatureEnabled,
} from "@/lib/use-features";
import { SessionRow, type SessionRowData } from "./session-row";
import { ScorecardPanel } from "./scorecard-panel";
import { TranscriptForm } from "./rd-chat-transcript-form";

const SURFACE_PAGE_SIZE = 15;
const ALL_SESSION_TYPES = "all";

type SessionTypeFilter = typeof ALL_SESSION_TYPES | "initial" | "follow_up";

function normalizeSearchText(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLowerCase();
}

interface SessionsPanelProps {
  sessions: SessionRowData[];
  rdId: string;
  rdSlug: string;
  /** Whether the "add transcript" form is open (owned by the tab container). */
  addOpen: boolean;
  setAddOpen: (open: boolean) => void;
  /**
   * Workspace surface mode: the list runs full height (the page scrolls)
   * instead of scrolling inside a fixed-height card viewport.
   */
  surface?: boolean;
}

/**
 * The Sessions tab content: the scorecard list, its inline add-transcript
 * form, and the scorecard slide-out. The card chrome (band, tabs, collapse,
 * the "+ Add Transcript" button) lives in the parent tab container.
 */
export function SessionsPanel({
  sessions,
  rdId,
  rdSlug,
  addOpen,
  setAddOpen,
  surface = false,
}: SessionsPanelProps) {
  const queryClient = useQueryClient();
  const sessionPatientLinksEnabled = useFeatureEnabled(
    SESSION_PATIENT_LINKS_FLAG,
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [sessionTypeFilter, setSessionTypeFilter] =
    useState<SessionTypeFilter>(ALL_SESSION_TYPES);
  const deferredQuery = useDeferredValue(query);
  const normalizedQuery = normalizeSearchText(deferredQuery);
  const filteredSessions = surface
    ? sessions.filter((session) => {
        if (
          sessionTypeFilter !== ALL_SESSION_TYPES &&
          session.sessionType !== sessionTypeFilter
        ) {
          return false;
        }

        if (!normalizedQuery) return true;
        return normalizeSearchText(session.clientName ?? "Untitled Session")
          .includes(normalizedQuery);
      })
    : sessions;
  const pageCount = Math.max(1, Math.ceil(filteredSessions.length / SURFACE_PAGE_SIZE));
  const currentPage = surface ? Math.min(page, pageCount) : 1;
  const pageStart = (currentPage - 1) * SURFACE_PAGE_SIZE;
  const visibleSessions = surface
    ? filteredSessions.slice(pageStart, pageStart + SURFACE_PAGE_SIZE)
    : filteredSessions;
  const firstVisible = filteredSessions.length === 0 ? 0 : pageStart + 1;
  const lastVisible = Math.min(pageStart + visibleSessions.length, filteredSessions.length);

  useEffect(() => {
    setPage((previous) => Math.min(previous, pageCount));
  }, [pageCount]);

  useEffect(() => {
    setPage(1);
  }, [normalizedQuery, sessionTypeFilter]);

  const selectedSession = selectedId
    ? sessions.find((s) => s.id === selectedId) ?? null
    : null;

  const handleSelect = useCallback((sessionId: string) => {
    setSelectedId((prev) => (prev === sessionId ? null : sessionId));
  }, []);

  const handleClose = useCallback(() => setSelectedId(null), []);

  return (
    <>
      {addOpen && (
        <TranscriptForm
          endpoint={`/api/rds/${rdId}/sessions`}
          variant="inline"
          title="Add session transcript"
          submitLabel="Submit & Analyze"
          requireSessionDate
          onSuccess={(result) => {
            const label = result.clientName || "Session";
            const datePart = result.sessionDate ? ` ${result.sessionDate}` : "";
            toast.success(`${label}${datePart} added`);
            setAddOpen(false);
            void queryClient.invalidateQueries({ queryKey: ["rd-sessions", rdId] });
            void queryClient.invalidateQueries({ queryKey: ["rd", rdId] });
          }}
          onClose={() => setAddOpen(false)}
        />
      )}

      {surface && sessions.length > 0 && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative w-full sm:max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-sand-400" />
            <Input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search sessions..."
              aria-label="Search sessions"
              className="h-10 rounded-[10px] border-sand-200 bg-white pl-10 pr-3 text-sm shadow-sm placeholder:text-sand-400"
            />
          </div>
          <div className="w-full sm:w-56">
            <Select
              value={sessionTypeFilter}
              onValueChange={(value) => setSessionTypeFilter(value as SessionTypeFilter)}
            >
              <SelectTrigger
                aria-label="Filter by session type"
                className="h-10 rounded-[10px] border-sand-200 bg-white text-sm shadow-sm"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_SESSION_TYPES}>All session types</SelectItem>
                <SelectItem value="initial">Initial</SelectItem>
                <SelectItem value="follow_up">Follow-up</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {filteredSessions.length > 0 && (
        <div
          className={
            surface
              ? "border-t border-sand-200 bg-card"
              : "max-h-[200px] overflow-y-auto"
            }
        >
          {visibleSessions.map((session) => (
            <SessionRow
              key={session.id}
              rdId={rdId}
              rdSlug={rdSlug}
              session={session}
              showPatientLinkStatus={sessionPatientLinksEnabled}
              isSelected={selectedId === session.id}
              onSelect={handleSelect}
            />
          ))}
        </div>
      )}
      {surface && sessions.length > 0 && filteredSessions.length === 0 && (
        <div className="rounded-xl border border-sand-200 bg-white px-5 py-10 text-center text-sm text-sand-500 shadow-sm">
          No sessions match your filters.
        </div>
      )}
      {surface && filteredSessions.length > 0 && (
        <div className="flex items-center justify-between gap-3 pb-2 pt-5">
          <p className="text-xs text-sand-400">
            Showing {firstVisible}-{lastVisible} of {filteredSessions.length} session
            {filteredSessions.length === 1 ? "" : "s"}
          </p>
          {pageCount > 1 && (
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPage((previous) => Math.max(1, previous - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span className="text-xs text-sand-400">
                Page {currentPage} of {pageCount}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPage((previous) => Math.min(pageCount, previous + 1))}
                disabled={currentPage === pageCount}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      )}

      <ScorecardPanel
        rdId={rdId}
        rdSlug={rdSlug}
        session={selectedSession}
        open={selectedSession !== null}
        onClose={handleClose}
      />
    </>
  );
}
